import { Entitlements } from '@experiences/constants';
import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import { getEnvVariableValue } from '@experiences/util';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    matchPath,
    useLocation,
} from 'react-router';

import { useEntitlements } from '../../component/entitlements/EntitlementsContextProvider';
import { isAdminSelector } from '../../store/selectors';
import { useIsSettingsRouteMatched } from '../../util/RouteUtil';
import * as RouteNames from '../constants/RouteNames';
import useIsProfileRoute from './useIsProfileRoute';

const useProductName = () => {

    const isProfileRoute = useIsProfileRoute();
    const isSettingsRoute = useIsSettingsRouteMatched();
    const { pathname } = useLocation();
    const isTenantCreateRoute = matchPath({ path: RouteNames.TenantCreate }, pathname);
    const { hasEntitlement } = useEntitlements();
    const isAdmin = useSelector(isAdminSelector);

    const EnableTestCloudOrganizationAndUserEntitlements = useFeatureFlagValue(Features.EnableTestCloudOrganizationAndUserEntitlements.name);

    const isTestCloudUser = useMemo(() => {
        if (EnableTestCloudOrganizationAndUserEntitlements) {
            return isAdmin ? hasEntitlement(Entitlements.EligibleForOrganizationExperience) : hasEntitlement(Entitlements.EligibleForUserExperience);
        }
        return hasEntitlement(Entitlements.AppTester) || hasEntitlement(Entitlements.AppTestDeveloper);
    }, [ EnableTestCloudOrganizationAndUserEntitlements, hasEntitlement, isAdmin ]);

    const showTrademark = useMemo(
        () => !isProfileRoute && !isSettingsRoute && !isTenantCreateRoute,
        [ isProfileRoute, isSettingsRoute, isTenantCreateRoute ],
    );

    const productName = useMemo<string>(() => {
        if (isProfileRoute) {
            return '';
        }

        if (isSettingsRoute || isTenantCreateRoute) {
            return 'Administration';
        }

        if (isTestCloudUser) {
            return 'Test Cloud';
        }

        return getEnvVariableValue('PRODUCT_NAME');
    }, [ isProfileRoute, isSettingsRoute, isTenantCreateRoute, isTestCloudUser ]);

    return {
        productName,
        showTrademark,
    };
};

export default useProductName;
